import { FC, Fragment, useRef } from "react"
import { Dialog, Transition } from "@headlessui/react"
import { DialogTheme } from "../../../types"

interface Props {
  open: boolean
  title: string
  body: string
  okLabel: string
  cancelLabel?: string
  onOk: () => void
  onCancel?: () => void
  onClose: () => void
  theme?: DialogTheme
}

const BBDialog: FC<Props> = ({
  open,
  title,
  body,
  onCancel,
  onClose,
  onOk,
  okLabel,
  cancelLabel,
  theme = "warning"
}) => {
  const cancelButtonRef = useRef(null)

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        initialFocus={cancelButtonRef}
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden bg-white text-left shadow-xl transition-all rounded-md sm:my-8 sm:w-full sm:max-w-xl">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-8 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left">
                      <Dialog.Title
                        as="h3"
                        className="text-xl font-medium leading-6 text-base-dark"
                      >
                        {title}
                      </Dialog.Title>
                      <div className="mt-6 text-base-dark">
                        <p className="text-default text-base-dark">{body}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <hr className="text-gray-300 mt-10" />
                <div className="bg-white px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                  <button
                    type="button"
                    className={`inline-flex justify-center items-center left-0 top-0 rounded-md border border-transparent ${
                      theme === "warning"
                        ? "bg-red-500 hover:bg-red-700"
                        : "bg-primary-400 hover:bg-primary-600"
                    } px-4 py-2 text-base font-medium text-white shadow-sm focus:outline-none focus:ring-0 sm:ml-3 sm:w-auto sm:text-md`}
                    onClick={onOk}
                  >
                    {okLabel}
                  </button>
                  {cancelLabel && (
                    <button
                      type="button"
                      className="mt-3 inline-flex justify-center items-center rounded-md bg-gray-100 px-4 py-2 text-default font-medium text-gray-700 shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-0 sm:mt-0 sm:ml-3 sm:w-auto sm:text-md"
                      onClick={onCancel}
                      ref={cancelButtonRef}
                    >
                      {cancelLabel}
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export default BBDialog
